import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Upload, Trash2, Loader2, ImageIcon, Radio } from "lucide-react";

export default function Branding() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const pick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    setPreview(URL.createObjectURL(file));
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post("/branding/logo", form);
      await checkAuth();
      toast.success("Logo updated");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Upload failed");
      setPreview(null);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete("/branding/logo");
      await checkAuth();
      setPreview(null);
      toast.success("Logo removed");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const currentLogo = preview || (user?.logo_url ? `${BACKEND_URL}${user.logo_url}` : null);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-20 bg-white/90 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center gap-4">
          <button onClick={() => navigate("/app")} data-testid="branding-back-button" className="p-2 rounded-lg hover:bg-slate-100 transition-all">
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <h1 className="font-display font-bold text-lg text-slate-900">Branding</h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10" data-testid="branding-page">
        <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
          <h2 className="font-display text-xl font-bold text-slate-900">Company logo</h2>
          <p className="text-slate-500 text-sm mt-1">
            Your logo appears on your dashboard, the live presentation screen and the audience join view. PNG, JPG, WEBP, GIF or SVG · up to 5MB.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row items-center gap-8">
            <div className="w-48 h-28 rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0" data-testid="logo-preview">
              {currentLogo ? (
                <img src={currentLogo} alt="Company logo" className="max-h-24 max-w-[170px] object-contain" />
              ) : (
                <div className="flex flex-col items-center text-slate-300">
                  <ImageIcon className="w-8 h-8" />
                  <span className="text-xs mt-1">No logo yet</span>
                </div>
              )}
            </div>

            <div className="flex-1 w-full space-y-3">
              <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="logo-file-input" />
              <button onClick={pick} disabled={busy} data-testid="logo-upload-button"
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl px-5 py-2.5 shadow-md shadow-indigo-200 transition-all disabled:opacity-60">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {user?.logo_url ? "Replace logo" : "Upload logo"}
              </button>
              {user?.logo_url && (
                <button onClick={remove} disabled={busy} data-testid="logo-remove-button"
                        className="w-full sm:w-auto sm:ml-3 inline-flex items-center justify-center gap-2 bg-white hover:bg-red-50 text-slate-600 hover:text-red-600 font-medium rounded-xl px-4 py-2.5 border border-slate-200 transition-all">
                  <Trash2 className="w-4 h-4" /> Remove
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 rounded-2xl bg-slate-950 p-6 flex items-center gap-4">
          <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">Preview on screen</span>
          <div className="ml-auto flex items-center gap-2 bg-white/10 rounded-xl px-4 py-2">
            {currentLogo ? (
              <img src={currentLogo} alt="logo" className="h-7 w-auto max-w-[120px] object-contain" />
            ) : (
              <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-white"><Radio className="w-3.5 h-3.5" /></span>
            )}
            <span className="font-display font-bold text-white">Pulse</span>
          </div>
        </div>
      </main>
    </div>
  );
}
